import { create } from 'zustand';
import {
  InventoryGroup,
  InventoryGroupType,
  InventorySection,
  InventoryCategory,
  InventorySectionProduct,
  AdditionalCountUnit,
  AvailableProduct,
  AvailableCustomIngredient,
  AvailableSubrecipe,
  InventoryProductSource,
  InventoryCount,
  CurrentValueResponse,
  AssignProductsResult,
  InventorySessionSummary,
  CountReviewResponse,
  fetchSessions,
  fetchCountReview,
  fetchSectionsByRestaurant,
  fetchGroupsByRestaurant,
  fetchCategoriesByRestaurant,
  fetchProductsBySection,
  fetchAvailableProducts,
  fetchAvailableCustomIngredients,
  fetchAvailableSubrecipes,
  fetchCurrentValue,
  fetchCountsByRestaurant,
  createGroup as apiCreateGroup,
  updateGroup as apiUpdateGroup,
  deleteGroup as apiDeleteGroup,
  createCategory as apiCreateCategory,
  updateCategory as apiUpdateCategory,
  deleteCategory as apiDeleteCategory,
  updateCategoryPositions as apiUpdateCategoryPositions,
  createSection as apiCreateSection,
  updateSection as apiUpdateSection,
  deleteSection as apiDeleteSection,
  assignProductsToSection as apiAssignProducts,
  updateSectionProductPositions as apiUpdatePositions,
  removeProductFromSection as apiRemoveProduct,
  relinkSectionProduct as apiRelinkSectionProduct,
  relinkSectionProductByKey as apiRelinkSectionProductByKey,
  updateSectionProductUnits as apiUpdateProductUnits,
  renameSectionProduct as apiRenameSectionProduct,
  setProductCategory as apiSetProductCategory,
  createCount as apiCreateCount,
  deleteCounts as apiDeleteCounts,
  setCountProductPrice as apiSetCountProductPrice,
  setInventoryPriceOverride as apiSetInventoryPriceOverride,
  setInventoryProductMinimum as apiSetProductMinimum,
  deleteInventoryProductMinimum as apiDeleteProductMinimum,
  updateCountQuantities as apiUpdateCountQuantities,
  addCountItems as apiAddCountItems,
  deleteCountItem as apiDeleteCountItem,
  renameCountItem as apiRenameCountItem,
  updateCountsDate as apiUpdateCountsDate,
  ImportPlan,
  ImportDryRunSummary,
  ImportResolution,
  ImportCommitSummary,
  ImportRebuildPreviewRow,
  fetchGroupProductUnits,
} from '../services/inventory/inventoryService';
import { baseProductKey } from '../utils/inventoryProductKey';
import { CanonicalCountUnit } from '../utils/reports/inventoryVariance';

/** In-memory snapshot of the Excel-import wizard so closing the modal (e.g. clicking
 *  outside after processing) doesn't lose the parsed plan + review progress. Kept only
 *  until page refresh. The picked File isn't restorable, but isn't needed post-dry-run. */
export interface ImportWizardSnapshot {
  restaurantId: string;
  step: 'upload' | 'review' | 'commit' | 'done';
  mode: 'counts' | 'minimums' | 'rebuild' | 'suppliers' | 'complete';
  plan: ImportPlan | null;
  summary: ImportDryRunSummary | null;
  /** Rebuild mode only: the dry-run's per-section keep/add/remove preview. */
  rebuildPreview: ImportRebuildPreviewRow[] | null;
  resolutions: Record<number, ImportResolution>;
  applyCounts: boolean;
  commitSummary: ImportCommitSummary | null;
  onlyWithStock: boolean;
  showImported: boolean;
}

interface InventoryStore {
  groups: InventoryGroup[];
  sections: InventorySection[];
  categories: InventoryCategory[];
  sectionProducts: Record<string, InventorySectionProduct[]>;
  /**
   * groupId → BASE product key → the group's STARRED count unit, for the leakage tab's
   * count-unit view. Loaded lazily per group and never invalidated within a session: a unit
   * change goes through the Manage page, which reloads the app's inventory state anyway.
   */
  canonicalUnitsByGroup: Record<string, Map<string, CanonicalCountUnit>>;
  availableProducts: AvailableProduct[];
  availableCustomIngredients: AvailableCustomIngredient[];
  availableSubrecipes: AvailableSubrecipe[];
  currentValue: CurrentValueResponse | null;
  countsHistory: InventoryCount[];

  // Count Review aggregation (single endpoint) for the current viewing selection.
  // `selection` null groupId = All groups, null sessionId = Latest-per-group, no
  // baselineSessionId = compared against the automatic previous count.
  // Single-entry cache: `loadedKey` = restaurantId|groupId|sessionId[|baseline]; refetch on miss.
  countReview: {
    data: CountReviewResponse | null;
    selection: CountReviewSelection;
    isLoading: boolean;
    loadedKey: string | null;
  };
  // Saved sessions for the count selector (keyset-paginated "load more").
  sessionSummaries: InventorySessionSummary[];
  sessionsGroupId: string | null;   // group scope the summaries are for (null = All)
  sessionsHasMore: boolean;
  isLoadingSessions: boolean;

  currentRestaurantId: string | null;
  // Track which restaurant current-value / counts-history were last loaded for, so
  // the auto-load hooks can skip a refetch on remount (mirrors order/revenue stores).
  currentValueLoadedForRestaurantId: string | null;
  countsLoadedForRestaurantId: string | null;
  countsCompleteForRestaurantId: string | null;
  isLoadingSections: boolean;
  isLoadingAvailable: boolean;
  isLoadingCurrentValue: boolean;
  isLoadingCounts: boolean;
  error: string | null;

  loadGroups: (restaurantId: string) => Promise<void>;
  loadCategories: (restaurantId: string) => Promise<void>;
  loadSections: (restaurantId: string) => Promise<void>;
  loadSectionProducts: (sectionId: string) => Promise<void>;
  loadCanonicalUnits: (groupId: string, restaurantId: string) => Promise<void>;
  loadAvailableProducts: (restaurantId: string) => Promise<void>;
  loadAvailableCustomIngredients: (restaurantId: string) => Promise<void>;
  loadAvailableSubrecipes: (restaurantId: string) => Promise<void>;
  loadCurrentValue: (restaurantId: string) => Promise<void>;
  loadCountsHistory: (restaurantId: string, options?: { complete?: boolean }) => Promise<void>;
  loadCountReview: (restaurantId: string, selection: CountReviewSelection) => Promise<void>;
  loadSessionSummaries: (restaurantId: string, options?: { groupId?: string | null; append?: boolean }) => Promise<void>;

  createGroup: (payload: { restaurantId: string; name: string; groupType: InventoryGroupType; position?: number }) => Promise<InventoryGroup>;
  updateGroup: (id: string, payload: { name?: string; groupType?: InventoryGroupType; position?: number }) => Promise<InventoryGroup>;
  deleteGroup: (id: string, restaurantId: string) => Promise<void>;

  createCategory: (payload: { restaurantId: string; name: string; color?: string; position?: number }) => Promise<InventoryCategory>;
  updateCategory: (id: string, payload: { name?: string; color?: string; position?: number }) => Promise<InventoryCategory>;
  deleteCategory: (id: string, restaurantId: string) => Promise<void>;
  reorderCategories: (updates: { id: string; position: number }[]) => Promise<void>;

  createSection: (payload: { restaurantId: string; groupId: string; sectionName: string; position?: number }) => Promise<InventorySection>;
  updateSection: (id: string, payload: { sectionName?: string; position?: number; groupId?: string }) => Promise<InventorySection>;
  deleteSection: (id: string, restaurantId: string) => Promise<void>;

  /** `sectionIds[0]` is the drawer's own section; extra ids fan the same products out to more sections in ONE request. */
  assignProducts: (sectionIds: string[], products: { productKey: string; productName: string; source?: InventoryProductSource; customIngredientId?: string; subrecipeId?: string; inventoryCategoryId?: string | null; asVariant?: boolean }[], restaurantId: string) => Promise<AssignProductsResult>;
  updateProductPositions: (sectionId: string, positions: { id: string; position: number }[]) => Promise<void>;
  removeProduct: (productId: string, sectionId: string, restaurantId: string) => Promise<void>;
  removeProducts: (productIds: string[], sectionId: string, restaurantId: string) => Promise<string[]>;
  /** Re-point a section product at a different order/custom/subrecipe target (fixes "Not linked"). */
  relinkSectionProduct: (productId: string, payload: { productKey: string; source: InventoryProductSource; customIngredientId?: string; subrecipeId?: string }, restaurantId: string, sectionId?: string | null) => Promise<InventorySectionProduct>;
  /** Same fix keyed by (section, frozen count key) — for rows whose assignment is gone. */
  relinkSectionProductByKey: (sectionId: string, payload: { oldProductKey: string; productKey: string; source: InventoryProductSource; customIngredientId?: string; subrecipeId?: string; inventoryCategoryId?: string | null }, restaurantId: string) => Promise<void>;
  updateProductUnits: (productId: string, sectionId: string, payload: { countUnit: string; countUnitToStdFactor: number; additionalCountUnits: AdditionalCountUnit[] }) => Promise<InventorySectionProduct>;
  /** Rename the per-assignment display name (past counts keep their frozen name). */
  renameProduct: (productId: string, sectionId: string, productName: string) => Promise<InventorySectionProduct>;
  setProductCategory: (productId: string, sectionId: string, inventoryCategoryId: string | null) => Promise<InventorySectionProduct>;

  createCount: (payload: { restaurantId: string; inventorySectionId: string; items: { productKey: string; count: number }[] }) => Promise<InventoryCount>;
  deleteSession: (countIds: string[], restaurantId: string) => Promise<void>;
  /** Correct the counted quantities of a saved count (History tab "Edit count"). Units + frozen prices are kept. */
  updateCountQuantities: (countId: string, restaurantId: string, items: { productKey: string; count: number }[]) => Promise<InventoryCount>;
  /** Add forgotten products to a saved count (section page). Priced live by the server. */
  addCountProducts: (countId: string, restaurantId: string, items: { productKey: string; count: number; countUnit?: string }[]) => Promise<InventoryCount>;
  /** Remove a product counted by mistake from a saved count (section page row delete). */
  removeCountProduct: (countId: string, restaurantId: string, productKey: string) => Promise<InventoryCount>;
  /** Rename a product line inside a saved count (section page inline name edit). Display name only. */
  renameCountProduct: (countId: string, restaurantId: string, productKey: string, productName: string) => Promise<InventoryCount>;
  /** Re-date a session's counts (History tab "Edit date") — finalized on the wrong day. */
  updateSessionDate: (restaurantId: string, items: { id: string; countedAt: Date }[]) => Promise<void>;
  /** Fix a missing/zero frozen price inside a saved count (Count Review "Set price"). */
  setCountProductPrice: (countId: string, payload: { productKey: string; source: InventoryProductSource; priceExVat: number; taxRate?: number; inventorySectionProductId?: string | null }, restaurantId: string) => Promise<void>;
  /** Durable, restaurant-wide inventory valuation price (fallback when the order price is null/0). */
  setProductPriceOverride: (restaurantId: string, payload: { productKey: string; priceExVat: number; countUnit?: string; taxRate?: number; inventorySectionProductId?: string | null }) => Promise<void>;
  /** Minimum stock per product, restaurant-wide across sections. `minStockCount` null clears it. */
  setProductMinimum: (restaurantId: string, payload: { productKey: string; minStockCount: number | null; inventorySectionProductId?: string | null }) => Promise<void>;
  /** Invalidate the count-review cache (and refetch if a review is shown) — used after count mutations. */
  invalidateCountReview: (restaurantId: string) => Promise<void>;

  isSectionsLoadedFor: (restaurantId: string) => boolean;
  isCurrentValueLoadedFor: (restaurantId: string) => boolean;
  isCountsLoadedFor: (restaurantId: string) => boolean;
  clear: () => void;

  // Excel-import wizard progress (persisted in-memory across modal close/reopen).
  importSession: ImportWizardSnapshot | null;
  setImportSession: (snapshot: ImportWizardSnapshot | null) => void;
  clearImportSession: () => void;
}

/** Page size for the count-selector "load more" session pagination. */
const SESSION_PAGE_SIZE = 20;

// A complete-history request can supersede a paginated one, and switching restaurants
// or clearing the store must prevent an older response from replacing the active data.
let countsRequestSequence = 0;
let countsRequest: { restaurantId: string; complete: boolean } | null = null;
let countReviewRequestSequence = 0;

/**
 * Count Review viewing selection. `baselineSessionId` = the explicit "Compared against" count;
 * absent/null = the automatic previous count, so every handler that builds a fresh
 * `{ groupId, sessionId }` resets it.
 */
type CountReviewSelection = { groupId: string | null; sessionId: string | null; baselineSessionId?: string | null };

// Single-entry cache key — an explicit baseline is part of it; the default adds nothing.
const countReviewKey = (restaurantId: string, sel: CountReviewSelection): string =>
  `${restaurantId}|${sel.groupId ?? 'all'}|${sel.sessionId ?? 'latest'}${sel.baselineSessionId ? `|${sel.baselineSessionId}` : ''}`;

export const useInventoryStore = create<InventoryStore>((set, get) => ({
  groups: [],
  sections: [],
  categories: [],
  sectionProducts: {},
  canonicalUnitsByGroup: {},
  availableProducts: [],
  availableCustomIngredients: [],
  availableSubrecipes: [],
  currentValue: null,
  countsHistory: [],

  countReview: { data: null, selection: { groupId: null, sessionId: null }, isLoading: false, loadedKey: null },
  sessionSummaries: [],
  sessionsGroupId: null,
  sessionsHasMore: false,
  isLoadingSessions: false,

  currentRestaurantId: null,
  currentValueLoadedForRestaurantId: null,
  countsLoadedForRestaurantId: null,
  countsCompleteForRestaurantId: null,
  isLoadingSections: false,
  isLoadingAvailable: false,
  isLoadingCurrentValue: false,
  isLoadingCounts: false,
  error: null,

  importSession: null,
  setImportSession: (snapshot) => set({ importSession: snapshot }),
  clearImportSession: () => set({ importSession: null }),

  loadGroups: async (restaurantId) => {
    try {
      const groups = await fetchGroupsByRestaurant(restaurantId);
      set({ groups });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load groups' });
    }
  },

  loadCategories: async (restaurantId) => {
    try {
      const categories = await fetchCategoriesByRestaurant(restaurantId);
      set({ categories });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load categories' });
    }
  },

  loadSections: async (restaurantId) => {
    if (get().isLoadingSections) return;
    set({ isLoadingSections: true, error: null });
    try {
      const [sections, groups, categories] = await Promise.all([
        fetchSectionsByRestaurant(restaurantId),
        fetchGroupsByRestaurant(restaurantId),
        fetchCategoriesByRestaurant(restaurantId),
      ]);
      set({ sections, groups, categories, currentRestaurantId: restaurantId, isLoadingSections: false });
    } catch (err) {
      set({ isLoadingSections: false, error: err instanceof Error ? err.message : 'Failed to load sections' });
    }
  },

  loadSectionProducts: async (sectionId) => {
    try {
      const products = await fetchProductsBySection(sectionId);
      set((state) => ({ sectionProducts: { ...state.sectionProducts, [sectionId]: products } }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load section products' });
    }
  },

  loadCanonicalUnits: async (groupId, restaurantId) => {
    if (get().canonicalUnitsByGroup[groupId]) return; // cached per group
    try {
      const products = await fetchGroupProductUnits(groupId, restaurantId);
      const units = new Map<string, CanonicalCountUnit>();
      // The endpoint answers with RAW product keys while the reports key by BASE key, so a
      // variant row's unit lands on the product its counts were recombined into.
      //
      // Tie-break: the exact base row wins, else the lexicographically SMALLEST raw key.
      // Deliberately not "first one seen" — this list arrives sorted by product name while the
      // report's twin (`loadCanonicalCountUnits`) walks assignments in section/product-position
      // order, so an order-dependent rule lets the page and the PDF pick different variants and
      // print the same product in two different units. Smallest-raw-key needs no shared order.
      const chosen = new Map<string, string>();
      for (const p of products) {
        if (!p.countUnit || !(p.countUnitToStdFactor > 0)) continue;
        const key = baseProductKey(p.productKey);
        const prev = chosen.get(key);
        const isBaseRow = p.productKey === key;
        if (prev !== undefined && (prev === key || (!isBaseRow && prev <= p.productKey))) continue;
        chosen.set(key, p.productKey);
        units.set(key, { countUnit: p.countUnit, stdPerCountUnit: p.countUnitToStdFactor, stdUnit: p.stdUnit });
      }
      set((state) => ({ canonicalUnitsByGroup: { ...state.canonicalUnitsByGroup, [groupId]: units } }));
    } catch (err) {
      // The tab silently keeps std units when this fails — there is no banner and no retry, so
      // the console is the ONLY signal that the count-unit view is degraded rather than empty.
      // Deliberately NOT written to `error`: that field drives the page's blocking error UI,
      // and a display preference failing to load must not take the leakage numbers off screen.
      console.error('Failed to load the group count units for the leakage count-unit view', err);
    }
  },

  loadAvailableProducts: async (restaurantId) => {
    if (get().isLoadingAvailable) return;
    set({ isLoadingAvailable: true, error: null });
    try {
      const availableProducts = await fetchAvailableProducts(restaurantId);
      set({ availableProducts, isLoadingAvailable: false });
    } catch (err) {
      set({ isLoadingAvailable: false, error: err instanceof Error ? err.message : 'Failed to load available products' });
    }
  },

  loadAvailableCustomIngredients: async (restaurantId) => {
    try {
      const availableCustomIngredients = await fetchAvailableCustomIngredients(restaurantId);
      set({ availableCustomIngredients });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load available custom ingredients' });
    }
  },

  loadAvailableSubrecipes: async (restaurantId) => {
    try {
      const availableSubrecipes = await fetchAvailableSubrecipes(restaurantId);
      set({ availableSubrecipes });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load available subrecipes' });
    }
  },

  loadCurrentValue: async (restaurantId) => {
    if (get().isLoadingCurrentValue) return;
    set({ isLoadingCurrentValue: true, error: null });
    try {
      const currentValue = await fetchCurrentValue(restaurantId);
      set({ currentValue, isLoadingCurrentValue: false, currentValueLoadedForRestaurantId: restaurantId });
    } catch (err) {
      set({ isLoadingCurrentValue: false, error: err instanceof Error ? err.message : 'Failed to load current value' });
    }
  },

  loadCountsHistory: async (restaurantId, options = {}) => {
    // Refreshing after an edit must not silently truncate an already complete cache.
    const complete = !!options.complete || get().countsCompleteForRestaurantId === restaurantId;
    if (countsRequest?.restaurantId === restaurantId && (countsRequest.complete || !complete)) return;
    const requestId = ++countsRequestSequence;
    countsRequest = { restaurantId, complete };
    set({
      isLoadingCounts: true, error: null,
      ...(get().countsLoadedForRestaurantId !== restaurantId ? {
        countsHistory: [], countsLoadedForRestaurantId: null, countsCompleteForRestaurantId: null,
      } : {}),
    });
    try {
      const countsHistory = await fetchCountsByRestaurant(restaurantId, complete ? { complete: true } : { limit: 100 });
      if (requestId !== countsRequestSequence) return;
      countsRequest = null;
      set({ countsHistory, isLoadingCounts: false, countsLoadedForRestaurantId: restaurantId,
        countsCompleteForRestaurantId: complete ? restaurantId : null });
    } catch (err) {
      if (requestId !== countsRequestSequence) return;
      countsRequest = null;
      set({ isLoadingCounts: false, error: err instanceof Error ? err.message : 'Failed to load counts history' });
    }
  },

  loadCountReview: async (restaurantId, selection) => {
    // A different restaurant can use the same selection. Invalidate every older
    // request, including when switching back to a cached review while one loads.
    const requestId = ++countReviewRequestSequence;
    const key = countReviewKey(restaurantId, selection);
    // Always reflect the requested selection so the header controls stay in sync.
    set((state) => ({ countReview: { ...state.countReview, selection } }));
    // Single-entry cache hit — keep the loaded payload, no refetch.
    if (get().countReview.loadedKey === key && get().countReview.data) {
      set((state) => ({ countReview: { ...state.countReview, isLoading: false } }));
      return;
    }
    set((state) => ({ countReview: { ...state.countReview, isLoading: true }, error: null }));
    try {
      const data = await fetchCountReview(restaurantId, {
        groupId: selection.groupId ?? undefined,
        sessionId: selection.sessionId ?? undefined,
        baselineSessionId: selection.baselineSessionId ?? undefined,
      });
      if (requestId !== countReviewRequestSequence) return;
      // Ignore a stale response if the selection changed while this was in flight.
      const curKey = countReviewKey(restaurantId, get().countReview.selection);
      if (curKey !== key) return;
      set((state) => ({ countReview: { ...state.countReview, data, loadedKey: key, isLoading: false } }));
    } catch (err) {
      if (requestId !== countReviewRequestSequence) return;
      set((state) => ({
        countReview: { ...state.countReview, isLoading: false },
        error: err instanceof Error ? err.message : 'Failed to load count review',
      }));
    }
  },

  loadSessionSummaries: async (restaurantId, options = {}) => {
    const { append = false } = options;
    // Only the "load more" (append) path needs the in-flight guard. A fresh scope
    // load (e.g. a group-tab switch) must always supersede so the selector reflects
    // the new group even if a prior fetch is still running.
    if (append && get().isLoadingSessions) return;
    // Append pages against the scope the loaded list is already for; a fresh load
    // uses the requested group scope.
    const groupId = append ? get().sessionsGroupId : (options.groupId ?? null);
    set({ isLoadingSessions: true });
    try {
      const existing = get().sessionSummaries;
      const beforeDate = append && existing.length > 0
        ? existing[existing.length - 1].completedAt.toISOString()
        : undefined;
      const page = await fetchSessions(restaurantId, {
        limit: SESSION_PAGE_SIZE,
        beforeDate,
        groupId: groupId ?? undefined,
      });
      set((state) => ({
        sessionSummaries: append ? [...state.sessionSummaries, ...page] : page,
        sessionsGroupId: groupId,
        sessionsHasMore: page.length === SESSION_PAGE_SIZE,
        isLoadingSessions: false,
      }));
    } catch (err) {
      set({ isLoadingSessions: false, error: err instanceof Error ? err.message : 'Failed to load inventory sessions' });
    }
  },

  createGroup: async (payload) => {
    const group = await apiCreateGroup(payload);
    set((state) => ({ groups: [...state.groups, group] }));
    return group;
  },

  updateGroup: async (id, payload) => {
    const group = await apiUpdateGroup(id, payload);
    set((state) => ({ groups: state.groups.map(g => g.id === id ? group : g) }));
    return group;
  },

  deleteGroup: async (id, restaurantId) => {
    await apiDeleteGroup(id);
    set((state) => ({ groups: state.groups.filter(g => g.id !== id) }));
    await get().loadCurrentValue(restaurantId);
  },

  createCategory: async (payload) => {
    const category = await apiCreateCategory(payload);
    set((state) => ({ categories: [...state.categories, category] }));
    return category;
  },

  updateCategory: async (id, payload) => {
    const category = await apiUpdateCategory(id, payload);
    set((state) => ({ categories: state.categories.map(c => c.id === id ? category : c) }));
    return category;
  },

  deleteCategory: async (id, restaurantId) => {
    await apiDeleteCategory(id);
    set((state) => ({ categories: state.categories.filter(c => c.id !== id) }));
    // Products linked to the deleted category became ungrouped server-side; refresh
    // any loaded section products so the UI reflects the detach.
    await get().loadSections(restaurantId);
    const loadedSectionIds = Object.keys(get().sectionProducts);
    await Promise.all(loadedSectionIds.map(sid => get().loadSectionProducts(sid)));
  },

  reorderCategories: async (updates) => {
    await apiUpdateCategoryPositions(updates);
    set((state) => {
      const posById = new Map(updates.map(u => [u.id, u.position]));
      const categories = state.categories
        .map(c => posById.has(c.id) ? { ...c, position: posById.get(c.id)! } : c)
        .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
      return { categories };
    });
  },

  createSection: async (payload) => {
    const section = await apiCreateSection(payload);
    set((state) => ({ sections: [...state.sections, section] }));
    return section;
  },

  updateSection: async (id, payload) => {
    const section = await apiUpdateSection(id, payload);
    set((state) => ({ sections: state.sections.map(s => s.id === id ? section : s) }));
    return section;
  },

  deleteSection: async (id, restaurantId) => {
    await apiDeleteSection(id);
    set((state) => {
      const { [id]: _, ...rest } = state.sectionProducts;
      return {
        sections: state.sections.filter(s => s.id !== id),
        sectionProducts: rest,
      };
    });
    // Refresh available products (assignment annotations) and current-value.
    await get().loadAvailableProducts(restaurantId);
    await get().loadAvailableCustomIngredients(restaurantId);
    await get().loadAvailableSubrecipes(restaurantId);
    await get().loadCurrentValue(restaurantId);
  },

  assignProducts: async (sectionIds, products, restaurantId) => {
    const [primarySectionId, ...extraSectionIds] = sectionIds;
    const result = await apiAssignProducts(primarySectionId, products, extraSectionIds);
    set((state) => {
      const sectionProducts = { ...state.sectionProducts };
      for (const row of result.inserted) {
        // Only patch sections whose list is actually loaded — writing into an unloaded key
        // would leave a one-row list that every other reader takes for the full section.
        const current = sectionProducts[row.inventorySectionId];
        if (!current) continue;
        sectionProducts[row.inventorySectionId] = [...current, row];
      }
      return { sectionProducts };
    });
    await get().loadAvailableProducts(restaurantId);
    await get().loadAvailableCustomIngredients(restaurantId);
    await get().loadAvailableSubrecipes(restaurantId);
    return result;
  },

  updateProductPositions: async (sectionId, positions) => {
    await apiUpdatePositions(sectionId, positions);
    set((state) => {
      const list = state.sectionProducts[sectionId] || [];
      const posById = new Map(positions.map(p => [p.id, p.position]));
      const updated = list
        .map(p => posById.has(p.id) ? { ...p, position: posById.get(p.id)! } : p)
        .sort((a, b) => a.position - b.position || a.productName.localeCompare(b.productName));
      return { sectionProducts: { ...state.sectionProducts, [sectionId]: updated } };
    });
  },

  removeProduct: async (productId, sectionId, restaurantId) => {
    await apiRemoveProduct(productId);
    set((state) => ({
      sectionProducts: {
        ...state.sectionProducts,
        [sectionId]: (state.sectionProducts[sectionId] || []).filter(p => p.id !== productId),
      },
    }));
    await get().loadAvailableProducts(restaurantId);
    await get().loadAvailableCustomIngredients(restaurantId);
    await get().loadAvailableSubrecipes(restaurantId);
  },

  removeProducts: async (productIds, sectionId, restaurantId) => {
    const failedIds: string[] = [];
    // Reuse assignment deletion, but refresh the product pickers only once per batch.
    for (const productId of Array.from(new Set(productIds))) {
      try {
        await apiRemoveProduct(productId);
        set((state) => ({
          sectionProducts: {
            ...state.sectionProducts,
            [sectionId]: (state.sectionProducts[sectionId] || []).filter(p => p.id !== productId),
          },
        }));
      } catch (err) {
        console.error('Failed to remove inventory section product:', err);
        failedIds.push(productId);
      }
    }
    if (productIds.length > 0) {
      await get().loadAvailableProducts(restaurantId);
      await get().loadAvailableCustomIngredients(restaurantId);
      await get().loadAvailableSubrecipes(restaurantId);
    }
    return failedIds;
  },

  relinkSectionProduct: async (productId, payload, restaurantId, sectionId) => {
    const updated = await apiRelinkSectionProduct(productId, payload);
    // Reflect the new link in the section-products cache (used by /inventory/edit) when loaded.
    if (sectionId) {
      set((state) => {
        const list = state.sectionProducts[sectionId];
        if (!list) return {};
        return {
          sectionProducts: {
            ...state.sectionProducts,
            [sectionId]: list.map((p) => (p.id === productId ? updated : p)),
          },
        };
      });
    }
    // Assignment annotations + the count-review aggregation both change on re-link.
    await get().loadAvailableProducts(restaurantId);
    await get().loadAvailableCustomIngredients(restaurantId);
    await get().loadAvailableSubrecipes(restaurantId);
    await get().invalidateCountReview(restaurantId);
    return updated;
  },

  relinkSectionProductByKey: async (sectionId, payload, restaurantId) => {
    await apiRelinkSectionProductByKey(sectionId, payload);
    // The assignment may have been re-created (not just updated), so refetch rather
    // than patching the cached list — but only when this section is actually loaded.
    if (get().sectionProducts[sectionId]) {
      await get().loadSectionProducts(sectionId);
    }
    await get().loadAvailableProducts(restaurantId);
    await get().loadAvailableCustomIngredients(restaurantId);
    await get().loadAvailableSubrecipes(restaurantId);
    await get().invalidateCountReview(restaurantId);
  },

  updateProductUnits: async (productId, sectionId, payload) => {
    const updated = await apiUpdateProductUnits(productId, payload);
    set((state) => ({
      sectionProducts: {
        ...state.sectionProducts,
        [sectionId]: (state.sectionProducts[sectionId] || []).map(p => p.id === productId ? updated : p),
      },
    }));
    return updated;
  },

  renameProduct: async (productId, sectionId, productName) => {
    const updated = await apiRenameSectionProduct(productId, { sectionId, productName });
    const sharedPrimaryName = updated.productKey === baseProductKey(updated.productKey);
    set((state) => ({
      sectionProducts: Object.fromEntries(Object.entries(state.sectionProducts).map(([id, products]) => [
        id,
        products.map(p => p.id === productId ? updated
          : sharedPrimaryName && p.restaurantId === updated.restaurantId && p.productKey === updated.productKey
            ? { ...p, productName: updated.productName } : p),
      ])),
    }));
    await get().invalidateCountReview(updated.restaurantId);
    return updated;
  },

  setProductCategory: async (productId, sectionId, inventoryCategoryId) => {
    const updated = await apiSetProductCategory(productId, inventoryCategoryId);
    set((state) => ({
      sectionProducts: {
        ...state.sectionProducts,
        [sectionId]: (state.sectionProducts[sectionId] || []).map(p => p.id === productId ? updated : p),
      },
    }));
    return updated;
  },

  createCount: async (payload) => {
    const count = await apiCreateCount(payload);
    set((state) => ({ countsHistory: [count, ...state.countsHistory] }));
    await get().loadCurrentValue(payload.restaurantId);
    await get().invalidateCountReview(payload.restaurantId);
    return count;
  },

  setCountProductPrice: async (countId, payload, restaurantId) => {
    await apiSetCountProductPrice(countId, payload);
    // The patched count changes the current-value snapshot + the review aggregation.
    await get().loadCurrentValue(restaurantId);
    await get().invalidateCountReview(restaurantId);
  },

  setProductPriceOverride: async (restaurantId, payload) => {
    await apiSetInventoryPriceOverride({ restaurantId, ...payload });
    // The durable price feeds the live product map, so every loaded section's enriched
    // prices are now stale — refresh them alongside the value/review aggregations.
    const loadedSectionIds = Object.keys(get().sectionProducts);
    await Promise.all(loadedSectionIds.map(sid => get().loadSectionProducts(sid)));
    await get().loadCurrentValue(restaurantId);
    await get().invalidateCountReview(restaurantId);
  },

  setProductMinimum: async (restaurantId, payload) => {
    const baseKey = baseProductKey(payload.productKey);
    let countUnit: string | null = null;
    if (payload.minStockCount === null) {
      await apiDeleteProductMinimum(restaurantId, payload.productKey);
    } else {
      const saved = await apiSetProductMinimum({
        restaurantId,
        productKey: payload.productKey,
        minStockCount: payload.minStockCount,
        inventorySectionProductId: payload.inventorySectionProductId,
      });
      countUnit = saved.countUnit;
    }
    // The minimum is restaurant-wide per product, so EVERY loaded section holding this
    // product (variant rows included) shows the new value without a refetch.
    set((state) => {
      const sectionProducts: Record<string, InventorySectionProduct[]> = {};
      for (const [sid, list] of Object.entries(state.sectionProducts)) {
        sectionProducts[sid] = list.map(p =>
          baseProductKey(p.productKey) === baseKey
            ? { ...p, minStockCount: payload.minStockCount, minStockCountUnit: countUnit }
            : p
        );
      }
      return { sectionProducts };
    });
    // The below-minimum tab / order list read these rows through the review aggregation.
    await get().invalidateCountReview(restaurantId);
  },

  updateCountQuantities: async (countId, restaurantId, items) => {
    const updated = await apiUpdateCountQuantities(countId, restaurantId, items);
    set((state) => ({
      countsHistory: state.countsHistory.map((c) => (c.id === updated.id ? updated : c)),
    }));
    // Corrected quantities change the current-value snapshot + the review aggregation.
    await get().loadCurrentValue(restaurantId);
    await get().invalidateCountReview(restaurantId);
    return updated;
  },

  addCountProducts: async (countId, restaurantId, items) => {
    const updated = await apiAddCountItems(countId, restaurantId, items);
    set((state) => ({
      countsHistory: state.countsHistory.map((c) => (c.id === updated.id ? updated : c)),
    }));
    await get().loadCurrentValue(restaurantId);
    await get().invalidateCountReview(restaurantId);
    return updated;
  },

  removeCountProduct: async (countId, restaurantId, productKey) => {
    const updated = await apiDeleteCountItem(countId, restaurantId, productKey);
    set((state) => ({
      countsHistory: state.countsHistory.map((c) => (c.id === updated.id ? updated : c)),
    }));
    await get().loadCurrentValue(restaurantId);
    await get().invalidateCountReview(restaurantId);
    return updated;
  },

  renameCountProduct: async (countId, restaurantId, productKey, productName) => {
    const updated = await apiRenameCountItem(countId, restaurantId, productKey, productName);
    set((state) => ({
      countsHistory: state.countsHistory.map((c) => (c.id === updated.id ? updated : c)),
    }));
    // Display name only — no value moves, so the current-value snapshot stays valid; the
    // review aggregation carries the name and has to be refetched.
    await get().invalidateCountReview(restaurantId);
    return updated;
  },

  deleteSession: async (countIds, restaurantId) => {
    await apiDeleteCounts(restaurantId, countIds);
    const idSet = new Set(countIds);
    set((state) => ({ countsHistory: state.countsHistory.filter((c) => !idSet.has(c.id)) }));
    // Deleting counts can change the latest-per-section snapshot, so refresh value.
    await get().loadCurrentValue(restaurantId);
    // The deleted session may be the "Compared against" baseline: fall back to the automatic
    // previous count rather than refetch against a session the server no longer has (404).
    set((state) => ({ countReview: { ...state.countReview, selection: { ...state.countReview.selection, baselineSessionId: null } } }));
    await get().invalidateCountReview(restaurantId);
  },

  updateSessionDate: async (restaurantId, items) => {
    const updated = await apiUpdateCountsDate(restaurantId, items);
    const byId = new Map(updated.map((c) => [c.id, c]));
    set((state) => ({
      countsHistory: state.countsHistory
        .map((c) => byId.get(c.id) ?? c)
        // History is date-ordered; a re-dated count belongs in its new slot.
        .sort((a, b) => b.countedAt.getTime() - a.countedAt.getTime()),
    }));
    // A new date can change which count is the latest per section (current value) and the
    // session selector's dates, on top of the review aggregation.
    await get().loadCurrentValue(restaurantId);
    await get().loadSessionSummaries(restaurantId, { groupId: get().sessionsGroupId });
    // A new date can move the "Compared against" baseline past the viewed count (400).
    set((state) => ({ countReview: { ...state.countReview, selection: { ...state.countReview.selection, baselineSessionId: null } } }));
    await get().invalidateCountReview(restaurantId);
  },

  invalidateCountReview: async (restaurantId) => {
    // Drop the single-entry cache so the next load refetches; if the page is
    // currently showing a review, re-fetch it immediately for the same selection.
    const showing = get().countReview.data != null;
    const selection = get().countReview.selection;
    set((state) => ({ countReview: { ...state.countReview, loadedKey: null } }));
    if (showing) await get().loadCountReview(restaurantId, selection);
  },

  isSectionsLoadedFor: (restaurantId) => get().currentRestaurantId === restaurantId,
  isCurrentValueLoadedFor: (restaurantId) => get().currentValueLoadedForRestaurantId === restaurantId,
  isCountsLoadedFor: (restaurantId) => get().countsLoadedForRestaurantId === restaurantId,

  clear: () => {
    ++countsRequestSequence;
    ++countReviewRequestSequence;
    countsRequest = null;
    set({
      groups: [],
      sections: [],
      categories: [],
      sectionProducts: {},
      canonicalUnitsByGroup: {},
      availableProducts: [],
      availableCustomIngredients: [],
      availableSubrecipes: [],
      currentValue: null,
      countsHistory: [],
      countReview: { data: null, selection: { groupId: null, sessionId: null }, isLoading: false, loadedKey: null },
      sessionSummaries: [],
      sessionsGroupId: null,
      sessionsHasMore: false,
      isLoadingSessions: false,
      currentRestaurantId: null,
      currentValueLoadedForRestaurantId: null,
      countsLoadedForRestaurantId: null,
      countsCompleteForRestaurantId: null,
      isLoadingCounts: false,
      error: null,
      importSession: null,
    });
  },
}));
